/**
 * File: appCheckService.ts
 * Path: packages/config/src/firebase/appCheckService.ts
 * Description: Firebase App Check (reCAPTCHA v3) client initialization and header helper.
 * Replaces the previous Cloudflare Turnstile bot-verification approach.
 */

/// <reference types="vite/client" />

import {
  initializeAppCheck,
  getToken,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";
import { getFirebaseApp } from "./firebaseClient.config";

const isBrowser = (): boolean => typeof window !== "undefined";

const siteKey = (): string =>
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) || "";

const debugToken = (): string =>
  (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN as string | undefined) || "";

/**
 * Debug tokens are for local runs and automated tests only. A token left in
 * the .env a live site was built from would otherwise put the live site in
 * debug mode: its requests carry an unregistered debug token (403s in the
 * console) instead of a real reCAPTCHA check.
 */
export const allowsDebugToken = (hostname: string): boolean =>
  hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";

let _appCheck: AppCheck | null = null;
let _attempted = false;

/**
 * Initializes App Check once per browser session.
 * Silently no-ops during SSR or when no site key is configured, so
 * environments without the key keep working (server enforcement decides).
 */
export function initAppCheck(): AppCheck | null {
  if (!isBrowser() || _attempted) return _appCheck;
  _attempted = true;

  const key = siteKey();
  if (!key) {
    console.warn(
      "[app-check] VITE_RECAPTCHA_SITE_KEY is not set — App Check disabled.",
    );
    return null;
  }

  try {
    const token = debugToken();
    if (token && allowsDebugToken(window.location.hostname)) {
      // Registers a debug token for localhost / automated test runs.
      (globalThis as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
        token === "true" ? true : token;
    }

    _appCheck = initializeAppCheck(getFirebaseApp(), {
      provider: new ReCaptchaV3Provider(key),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    console.warn("[app-check] initialization failed:", err);
    _appCheck = null;
  }

  return _appCheck;
}

/**
 * Returns the App Check header for manual `fetch` calls to HTTP functions.
 * Resolves to an empty object when App Check is unavailable so callers
 * never break — the server decides whether to enforce.
 */
export async function getAppCheckHeaders(): Promise<Record<string, string>> {
  const instance = initAppCheck();
  if (!instance) return {};

  try {
    const result = await getToken(instance, /* forceRefresh */ false);
    return result?.token ? { "X-Firebase-AppCheck": result.token } : {};
  } catch (err) {
    console.warn("[app-check] token retrieval failed:", err);
    return {};
  }
}
